import { useEffect, useMemo, useState } from 'react';
import { MdClose, MdFavorite, MdHeadset, MdInfoOutline, MdMusicNote, MdPeople, MdPersonAdd, MdRepeat } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

// The artist page's "Audiência" tab — the same figures the artist sees in the
// web studio (api/artist/audience.php → classes/ArtistAudience.php). Usage
// only, never money. The privacy rules (cities, age, gender) are applied on the
// server; this page only draws what comes back.

const PERIODS = [
    { key: '7d', label: '7 dias', vs: '7 dias anteriores' },
    { key: '30d', label: '30 dias', vs: '30 dias anteriores' },
    { key: '12m', label: '12 meses', vs: '12 meses anteriores' },
    { key: 'all', label: 'Desde sempre', vs: '' },
];
const SOURCE_LABEL = {
    search: 'Pesquisa', library: 'Biblioteca e favoritos de quem ouve', profile: 'Perfil do artista', playlist: 'Playlists',
    discovery: 'Início e recomendações', radio: 'Rádio', release: 'Página do lançamento', other: 'Outros (fila, links, directo)',
};
const PLATFORM_LABEL = { android: 'App Android', ios: 'App iPhone', web: 'Site (app.izenu.ao)', other: 'Outros' };
const AGE_LABEL = { '<18': 'Menos de 18', '18-24': '18–24', '25-34': '25–34', '35-44': '35–44', '45+': '45 ou mais' };
const GENDER_LABEL = { female: 'Mulheres', male: 'Homens', other: 'Outro' };
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const fmtCount = (n) => new Intl.NumberFormat('pt-PT').format(n || 0);
const bucketLabel = (key, unit) => {
    const [y, m, d] = key.split('-').map(Number);
    return unit === 'day' ? `${d} ${MONTHS[(m || 1) - 1]}` : `${MONTHS[(m || 1) - 1]} ${y}`;
};
let regionNames = null;
try { regionNames = new Intl.DisplayNames(['pt-PT'], { type: 'region' }); } catch (e) { /* older browsers: names as stored */ }
const countryName = (name, code) => {
    if (name === 'other') return 'Outros países';
    if (code && regionNames) { try { return regionNames.of(code.toUpperCase()) || name; } catch (e) { /* as stored */ } }
    return name;
};
const share = (n, total) => (total > 0 ? (n / total) * 100 : 0);

const card = 'rounded-xl !border !border-solid border-gray-200 bg-white';

function Stat({ icon, label, value, hint }) {
    return (
        <div className={`${card} min-w-0 p-4`}>
            <div className="mb-1.5 flex items-center gap-2 text-gray-500">
                <span className="shrink-0 text-amber-600">{icon}</span>
                <span className="truncate text-xs font-semibold uppercase tracking-wide">{label}</span>
            </div>
            <div className="break-words text-xl font-bold text-gray-900">{value}</div>
            {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
        </div>
    );
}

/** "2 out – 31 out" (or "nov 2025 – out 2026" with months): the dates a number covers. */
const fmtRange = (from, to, months = false) => {
    if (!from || !to) return '';
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    const a = months ? MONTHS[fm - 1] : `${fd} ${MONTHS[fm - 1]}`;
    const b = months ? MONTHS[tm - 1] : `${td} ${MONTHS[tm - 1]}`;
    if (fy !== ty || months) return `${a} ${fy} – ${b} ${ty}`;
    if (ty !== new Date().getFullYear()) return `${a} – ${b} ${ty}`;
    return `${a} – ${b}`;
};

/** "+12% vs 2 set – 1 out": the change against the period before, named by its dates. */
function Delta({ now, before, vs }) {
    if (!before) return now > 0 ? <span className="text-green-700">Novo · 0 em {vs}</span> : null;
    const pct = Math.round(((now - before) / before) * 100);
    if (pct === 0) return <span>Igual a {vs}</span>;
    return <span className={pct > 0 ? 'text-green-700' : 'text-red-600'}>{pct > 0 ? '+' : ''}{pct}% <span className="text-gray-500">vs {vs}</span></span>;
}

function Section({ title, hint, children }) {
    return (
        <section className={`${card} min-w-0 p-4 sm:p-5`}>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {hint && <p className="mb-3 text-xs text-gray-500">{hint}</p>}
            <div className={hint ? '' : 'mt-3'}>{children}</div>
        </section>
    );
}

function BarList({ rows, pct = false }) {
    const top = Math.max(1, ...rows.map((r) => r.pct));
    return (
        <ul className="space-y-3 [&>li]:!p-0">
            {rows.map((r) => (
                <li key={r.key}>
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                        <span className={`min-w-0 truncate ${r.muted ? 'text-gray-500' : 'text-gray-900'}`}>{r.label}</span>
                        <span className="whitespace-nowrap tabular-nums text-gray-600">
                            {pct ? (r.value < 5 ? '<5%' : `${r.value}%`) : <>{fmtCount(r.value)}<span className="text-gray-400"> · {r.pct > 0 && r.pct < 1 ? '<1' : Math.round(r.pct)}%</span></>}
                        </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className={`h-full rounded-full ${r.muted ? 'bg-gray-400' : 'bg-amber-500'}`} style={{ width: `${Math.max(pct || r.pct > 0 ? 2 : 0, (r.pct / top) * 100)}%` }} />
                    </div>
                </li>
            ))}
        </ul>
    );
}

/** Streams over time, one hue; hover or tap a column for the numbers. Dots mark releases. */
function ColumnChart({ points, unit, markers, partialKey }) {
    const [active, setActive] = useState(null);
    const max = Math.max(1, ...points.map((p) => p.streams));
    const p = active !== null ? points[active] : null;
    const ticks = points.length > 2 ? [0, Math.floor((points.length - 1) / 2), points.length - 1] : points.map((_, i) => i);
    return (
        <div className="relative select-none" onMouseLeave={() => setActive(null)}>
            <div className="mb-1 flex justify-between text-[11px] text-gray-400"><span className="tabular-nums">{fmtCount(max)}</span><span>{unit === 'day' ? 'por dia' : 'por mês'}</span></div>
            <div className="relative flex h-40 items-end gap-[2px] border-b border-gray-200">
                {points.map((pt, i) => (
                    <button key={pt.key} type="button" className="group relative flex h-full flex-1 items-end justify-center focus:outline-none"
                        onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(active === i ? null : i)}
                        aria-label={`${bucketLabel(pt.key, unit)}: ${pt.streams} reproduções`}>
                        <span className={`w-full max-w-[28px] rounded-t-[4px] ${active === i ? 'bg-amber-600' : 'bg-amber-500 group-hover:bg-amber-600'}`}
                            style={{ height: pt.streams ? `${Math.max(2, (pt.streams / max) * 100)}%` : 0 }} />
                        {markers[pt.key] && <span className="absolute -bottom-[7px] h-1.5 w-1.5 rounded-full bg-violet-600" />}
                    </button>
                ))}
            </div>
            <div className="relative mt-2 h-5 text-[11px] text-gray-400">
                {ticks.map((i) => (
                    <span key={i} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((i + 0.5) / points.length) * 100}%` }}>{bucketLabel(points[i].key, unit)}</span>
                ))}
            </div>
            {p && (
                <div className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg"
                    style={{ left: `${Math.min(85, Math.max(15, ((active + 0.5) / points.length) * 100))}%` }}>
                    <p className="text-gray-300">{bucketLabel(p.key, unit)}{p.key === partialKey && ' · até hoje'}</p>
                    <p className="font-semibold tabular-nums">{fmtCount(p.streams)} {p.streams === 1 ? 'reprodução' : 'reproduções'}</p>
                    <p className="tabular-nums text-gray-300">{fmtCount(p.listeners)} {p.listeners === 1 ? 'ouvinte' : 'ouvintes'}</p>
                    {(markers[p.key] || []).map((t) => <p key={t} className="mt-0.5 text-violet-300">Lançamento: {t}</p>)}
                </div>
            )}
        </div>
    );
}

function Demo({ title, groups, labels }) {
    return (
        <Section title={title}>
            {groups.available ? (
                <>
                    <BarList pct rows={groups.groups.map((g) => ({ key: g.key, label: labels[g.key] || g.key, value: g.pct, pct: g.pct }))} />
                    <p className="mt-4 text-xs text-gray-400">Com base em cerca de {groups.coverage_pct}% dos ouvintes, que o indicaram no perfil.{groups.hidden_small && ' Grupos com muito poucas pessoas não são mostrados.'}</p>
                </>
            ) : (
                <p className="text-sm text-gray-500">{groups.reason === 'groups' ? 'Os ouvintes estão quase todos num só grupo — mostrá-lo identificaria pessoas.' : 'Ainda poucos ouvintes indicaram esta informação no perfil.'}</p>
            )}
        </Section>
    );
}

export default function AudienceTab({ artistId }) {
    const [period, setPeriod] = useState('30d');
    const [musicId, setMusicId] = useState(null);
    const [data, setData] = useState(null);
    const [tracks, setTracks] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        izenuAPI.get('/distributor/artist/audience.php', { params: { artist_id: artistId, period, ...(musicId ? { music_id: musicId } : {}) } })
            .then(({ data: d }) => {
                if (!alive) return;
                setData(d);
                if (!musicId || d.tracks.length) setTracks(d.tracks);
            })
            .catch((err) => alive && setError(err?.response?.data?.message || 'Não foi possível carregar a audiência.'))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [artistId, period, musicId]);

    const markers = useMemo(() => {
        const out = {};
        if (!data) return out;
        data.releases.forEach((r) => {
            const k = data.series.unit === 'day' ? r.date : r.date.slice(0, 7);
            (out[k] = out[k] || []).push(r.title);
        });
        return out;
    }, [data]);

    if (error && !data) return <p className="py-12 text-center text-sm text-gray-500">{error}</p>;
    if (!data) return <p className="py-12 text-center text-sm text-gray-500">A carregar…</p>;

    const periodInfo = PERIODS.find((p) => p.key === period);
    // The dates behind every number, and the ones they are compared with.
    const months = data.period === '12m';
    const range = fmtRange(data.from, data.to, months);
    const prevRange = fmtRange(data.prev_from, data.prev_to, months);
    const t = data.totals;
    const total = t.streams;
    const selected = tracks.find((tr) => tr.music_id === musicId);
    const d = data.demographics;
    const demoHint = `Só com ${data.rules.demo_min_listeners}+ ouvintes, em percentagens arredondadas, sem grupos com menos de ${data.rules.group_min} pessoas.`;

    return (
        <div className={`space-y-5 ${loading ? 'opacity-60' : ''}`}>
            <div className="flex flex-wrap items-center gap-2">
                {PERIODS.map((p) => (
                    <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
                        className={`rounded-full px-3.5 py-1.5 text-sm font-medium !border !border-solid ${period === p.key ? 'border-amber-500 bg-amber-500 !text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {p.label}
                    </button>
                ))}
                <div className="flex items-center gap-2 sm:ml-auto">
                    <label htmlFor="aud-track" className="text-xs text-gray-500">Música</label>
                    <select id="aud-track" value={musicId || ''} onChange={(e) => setMusicId(e.target.value ? Number(e.target.value) : null)}
                        className="max-w-[220px] rounded-lg border-gray-300 py-1.5 text-sm">
                        <option value="">Todas as músicas</option>
                        {tracks.map((tr) => <option key={tr.music_id} value={tr.music_id}>{tr.title}</option>)}
                    </select>
                    {musicId && <button type="button" onClick={() => setMusicId(null)} aria-label="Todas as músicas" className="text-gray-400 hover:text-gray-700"><MdClose size={18} /></button>}
                </div>
            </div>
            <p className="-mt-2 text-xs text-gray-500">
                {data.period === 'all' ? <>Desde a primeira reprodução até hoje</> : (
                    <><strong className="font-medium text-gray-800">{range}</strong>{months && ' (o mês actual até hoje)'} · comparado com {prevRange}</>
                )}
                <span className="text-gray-400"> · contam reproduções a partir de 30 segundos, sem as do próprio artista</span>
            </p>
            {selected && <p className="-mt-2 text-sm text-gray-600">A ver só <strong>{selected.title}</strong>. Seguidores são do perfil, não da música.</p>}

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat icon={<MdHeadset size={18} />} label="Reproduções" value={fmtCount(t.streams)}
                    hint={t.prev_streams !== null ? <Delta now={t.streams} before={t.prev_streams} vs={prevRange} /> : 'desde a primeira'} />
                <Stat icon={<MdPeople size={18} />} label="Ouvintes" value={fmtCount(t.listeners)}
                    hint={t.prev_listeners !== null ? <Delta now={t.listeners} before={t.prev_listeners} vs={prevRange} /> : null} />
                <Stat icon={<MdPersonAdd size={18} />} label="Seguidores" value={fmtCount(data.followers.total)}
                    hint={data.followers.new !== null ? `+${fmtCount(data.followers.new)} em ${range}` : null} />
                <Stat icon={<MdRepeat size={18} />} label="Fãs fiéis" value={fmtCount(data.fans.fans)}
                    hint={`ouviram em ${data.rules.fan_weeks}+ das últimas ${data.rules.fan_window_weeks} semanas (de ${fmtCount(data.fans.listeners)})`} />
            </div>

            <Section title={data.series.unit === 'day' ? 'Reproduções por dia' : 'Reproduções por mês'} hint="Passa o rato numa coluna para ver os números. Os pontos marcam lançamentos.">
                {total > 0 ? <ColumnChart points={data.series.points} unit={data.series.unit} markers={markers} partialKey={data.series.unit === 'month' ? data.to.slice(0, 7) : undefined} />
                    : <p className="py-6 text-center text-sm text-gray-500">Sem reproduções nos últimos {periodInfo.label}.</p>}
            </Section>

            {total > 0 && (
                <>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <Section title="De onde vêm as reproduções" hint="Onde estava quem ouviu quando carregou no play.">
                            <BarList rows={data.sources.map((s) => ({ key: s.key, label: SOURCE_LABEL[s.key] || s.key, value: s.streams, pct: share(s.streams, total), muted: s.key === 'other' }))} />
                        </Section>
                        <div className="min-w-0 space-y-4">
                            <Section title="Playlists" hint="As playlists privadas dos ouvintes aparecem juntas, sem nome.">
                                {data.playlists.length === 0 ? <p className="text-sm text-gray-500">Nenhuma reprodução veio de uma playlist neste período.</p>
                                    : <BarList rows={data.playlists.map((p, i) => ({
                                        key: String(p.id || `private-${i}`),
                                        label: p.id ? <>{p.title}{p.kind === 'editorial' && <span className="text-xs text-violet-600"> · Izenu</span>}{p.kind === 'own' && <span className="text-xs text-gray-400"> · do artista</span>}</> : 'Playlists privadas de ouvintes',
                                        value: p.streams, pct: share(p.streams, total), muted: !p.id,
                                    }))} />}
                            </Section>
                            <Section title="Plataforma">
                                <BarList rows={data.platforms.map((p) => ({ key: p.key, label: PLATFORM_LABEL[p.key] || p.key, value: p.streams, pct: share(p.streams, total), muted: p.key === 'other' }))} />
                            </Section>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <Section title="Países" hint="Pela localização da ligação à internet.">
                            <BarList rows={data.countries.map((c) => ({ key: c.country, label: countryName(c.country, c.code), value: c.streams, pct: share(c.streams, total), muted: c.country === 'other' }))} />
                        </Section>
                        <Section title="Cidades" hint={`Cidades com menos de ${data.rules.city_min} ouvintes aparecem em "Outras cidades".`}>
                            <BarList rows={data.cities.map((c, i) => ({
                                key: c.city ? `${c.city}-${c.country}` : `rest-${i}`,
                                label: c.city ? <>{c.city}{c.country && <span className="text-xs text-gray-400"> · {c.country}</span>}</> : 'Outras cidades',
                                value: c.streams, pct: share(c.streams, total), muted: !c.city,
                            }))} />
                        </Section>
                    </div>

                    {d.available ? (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <Demo title="Idade" groups={d.age} labels={AGE_LABEL} />
                            <Demo title="Género" groups={d.gender} labels={GENDER_LABEL} />
                        </div>
                    ) : (
                        <Section title="Idade e género" hint={demoHint}>
                            <p className="text-sm text-gray-500">
                                {d.reason === 'period' ? 'Em 7 dias são poucas pessoas para mostrar idade e género. Escolhe 30 dias ou mais.'
                                    : `Aparece com pelo menos ${d.min} ouvintes no período (agora: ${fmtCount(t.listeners)}). Experimenta "12 meses".`}
                            </p>
                        </Section>
                    )}
                </>
            )}

            <Section title="Por música" hint="Clica numa música para ver a página só para ela. Passaram dos 30 s: das vezes que carregaram no play, quantas chegaram aos 30 segundos (a partir de 10 plays).">
                {tracks.length === 0 ? <p className="text-sm text-gray-500">Nenhuma música teve actividade neste período.</p> : (
                    <div className="-mx-4 overflow-x-auto sm:-mx-5">
                        <table className="w-full min-w-[640px] text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase text-gray-500">
                                    <th className="px-4 py-2 font-semibold sm:px-5">Música</th>
                                    <th className="px-3 py-2 text-right font-semibold">Reproduções</th>
                                    <th className="px-3 py-2 text-right font-semibold">Ouvintes</th>
                                    <th className="px-3 py-2 text-right font-semibold"><span className="inline-flex items-center gap-1"><MdFavorite size={12} /> Gostos</span></th>
                                    <th className="px-3 py-2 text-right font-semibold">Em playlists</th>
                                    <th className="px-4 py-2 text-right font-semibold sm:px-5">Passaram dos 30 s</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tracks.map((tr) => (
                                    <tr key={tr.music_id} onClick={() => setMusicId(tr.music_id === musicId ? null : tr.music_id)}
                                        className={`cursor-pointer border-t border-gray-100 hover:bg-gray-50 ${musicId === tr.music_id ? 'bg-amber-50' : ''}`}>
                                        <td className="px-4 py-2.5 sm:px-5">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100 text-gray-400">
                                                    {tr.cover ? <img src={tr.cover} alt="" className="h-full w-full object-cover" /> : <MdMusicNote size={16} />}
                                                </div>
                                                <span className="truncate text-gray-900">{tr.title}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtCount(tr.streams)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtCount(tr.listeners)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtCount(tr.likes)}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{fmtCount(tr.playlist_adds)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-600 sm:px-5">{tr.past_30s_pct === null ? <span className="text-gray-400">—</span> : `${tr.past_30s_pct}%`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            <p className="flex items-start gap-2 text-xs text-gray-400">
                <MdInfoOutline size={14} className="mt-px shrink-0" />
                Reproduções a partir de 30 segundos, sem as do próprio artista nem contas excluídas por repetição artificial. Não é dinheiro: os royalties são calculados no fecho de cada mês.
            </p>
        </div>
    );
}
